import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ClientTestimonials = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "Robert Chen",
      position: "IT Director",
      company: "TechFlow Manufacturing",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop",
      content: `FXtreem Fibre Solutions transformed our entire network infrastructure with minimal disruption to our 24/7 manufacturing operations. Their team worked nights and weekends to ensure zero downtime during the transition. The performance improvement has been remarkable - our data transfer speeds increased by 400% and we've had zero connectivity issues since installation.`,
      metrics: {
        improvement: "400% Speed Increase",
        downtime: "Zero Downtime",
        timeline: "Completed 2 weeks early"
      },
      rating: 5
    },
    {
      id: 2,
      name: "Sarah Martinez",
      position: "Facilities Manager",
      company: "Regional Medical Center",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop",
      content: `The professionalism and expertise of the Xtreem Fibre Solutions team exceeded our expectations. Healthcare facilities have strict requirements for network reliability and security. They not only met all our compliance needs but provided comprehensive documentation that streamlined our regulatory audits. Their ongoing support has been exceptional.`,
      metrics: {
        improvement: "99.99% Uptime",
        downtime: "HIPAA Compliant",
        timeline: "Full documentation provided"
      },
      rating: 5
    },
    {
      id: 3,
      name: "Michael Thompson",
      position: "Operations Director",
      company: "DataCore Solutions",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop",
      content: `As a data center operator, we needed a partner who understood the critical nature of our infrastructure. Xtreem Fibre Solution's assessment identified optimization opportunities we hadn't considered, and their installation quality is impeccable. They've become our go-to partner for all fiber optic projects across our facilities.`,
      metrics: {
        improvement: "50% Latency Reduction",
        downtime: "Mission Critical Ready",
        timeline: "Ongoing partnership"
      },
      rating: 5
    },
    {
      id: 4,
      name: "Jennifer Park",
      position: "Chief Technology Officer",
      company: "Innovation Labs",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop",
      content: `Xtreem Fibre Solutions doesn't just install fiber - they provide strategic technology guidance. Their team helped us plan our network architecture for future growth and emerging technologies. The installation was flawless, and their post-installation support has been invaluable for our expanding operations.`,
      metrics: {
        improvement: "Future-Ready Infrastructure",
        downtime: "Scalable Architecture",
        timeline: "Strategic Partnership"
      },
      rating: 5
    }
  ];

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials?.length);
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials?.length) % testimonials?.length);
  };

  const currentTestimonial = testimonials?.[activeTestimonial];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-text-primary mb-4">
            Client Success Stories
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Our clients choose us for our reliability, expertise, and commitment to their success. Here's what they say about the Xtreem Fibre Solutions experience.
          </p>
        </div>
        
        <div className="relative">
          <div className="bg-surface rounded-2xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-3 gap-8 items-center">
              {/* Client Info */}
              <div className="text-center lg:text-left">
                <div className="inline-block relative mb-6">
                  <img
                    src={currentTestimonial?.image}
                    alt={currentTestimonial?.name}
                    className="w-24 h-24 rounded-full object-cover mx-auto lg:mx-0"
                  />
                  <div className="absolute -bottom-2 -right-2 bg-accent text-white p-2 rounded-full">
                    <Icon name="Quote" size={16} />
                  </div>
                </div>
                
                <h3 className="text-xl font-semibold text-text-primary">
                  {currentTestimonial?.name}
                </h3>
                <p className="text-primary font-medium">{currentTestimonial?.position}</p>
                <p className="text-text-secondary">{currentTestimonial?.company}</p>
                
                <div className="flex justify-center lg:justify-start mt-4">
                  {[...Array(currentTestimonial?.rating)]?.map((_, i) => (
                    <Icon key={i} name="Star" size={20} className="text-warning fill-current" />
                  ))}
                </div>
              </div>
              
              {/* Testimonial Content */}
              <div className="lg:col-span-2 space-y-6">
                <blockquote className="text-lg text-text-secondary leading-relaxed italic">
                  "{currentTestimonial?.content}"
                </blockquote>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="text-lg font-semibold text-primary">
                      {currentTestimonial?.metrics?.improvement}
                    </div>
                    <div className="text-sm text-text-secondary">Performance</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="text-lg font-semibold text-accent">
                      {currentTestimonial?.metrics?.downtime}
                    </div>
                    <div className="text-sm text-text-secondary">Reliability</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="text-lg font-semibold text-text-primary">
                      {currentTestimonial?.metrics?.timeline}
                    </div>
                    <div className="text-sm text-text-secondary">Delivery</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Navigation */}
          <div className="flex justify-center items-center mt-8 space-x-4">
            <button
              onClick={prevTestimonial}
              className="p-3 bg-white rounded-full shadow-brand-moderate hover:shadow-brand-strong transition-all duration-200 hover:bg-primary hover:text-white"
            >
              <Icon name="ChevronLeft" size={20} />
            </button>
            
            <div className="flex space-x-2">
              {testimonials?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === activeTestimonial ? 'bg-primary' : 'bg-border'
                  }`}
                />
              ))}
            </div>
            
            <button
              onClick={nextTestimonial}
              className="p-3 bg-white rounded-full shadow-brand-moderate hover:shadow-brand-strong transition-all duration-200 hover:bg-primary hover:text-white"
            >
              <Icon name="ChevronRight" size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientTestimonials;